/**
 * Consent checkbox for a Fellows Corner mentorship inquiry.
 *
 * Combines PII confirmation with Terms of Use agreement. Unlike the Medical On
 * Demand compliance checkbox, it carries no manufacturer-solicitation language
 * since mentorship inquiries are not product support.
 */

import { AlertCircleIcon, CheckIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Linking } from 'react-native';

import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { TERMS_OF_USE_URL } from '@/constants/links';
import type { MentorshipFormData } from '@/hooks/useMentorshipInquiryForm';

type MentorshipConsentCheckboxProps = {
  control: Control<MentorshipFormData>;
  errors: FieldErrors<MentorshipFormData>;
  isDisabled?: boolean;
};

export function MentorshipConsentCheckbox({
  control,
  errors,
  isDisabled = false,
}: MentorshipConsentCheckboxProps) {
  return (
    <Controller
      name="consentAcknowledged"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const showError =
          !!errors.consentAcknowledged?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <Checkbox
              testID="mentorship-consent-checkbox"
              className="items-start justify-start gap-3"
              value={value?.toString() || ''}
              onChange={(checked) => onChange(checked)}
              isChecked={value}
              size="sm"
              aria-label="Consent acknowledgment">
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>

              <CheckboxLabel className="-m-1 flex-1">
                <Text size="xs">
                  By submitting this question, I confirm this message does not contain
                  patient-identifiable information and I agree to Primum's{' '}
                  <LinkText
                    size="xs"
                    className="text-primary-500 underline hover:text-primary-600"
                    onPress={() => Linking.openURL(TERMS_OF_USE_URL)}>
                    Terms of Use
                  </LinkText>
                  .*
                </Text>
              </CheckboxLabel>
            </Checkbox>

            {showError && (
              <FormControlError className="mt-2">
                <FormControlErrorIcon as={AlertCircleIcon} />
                <FormControlErrorText size="xs">
                  {errors.consentAcknowledged?.message}
                </FormControlErrorText>
              </FormControlError>
            )}
          </FormControl>
        );
      }}
    />
  );
}
